import React, { createContext, PropsWithChildren, useCallback, useContext, useState } from 'react';
import {
  createTheme,
  ThemeOptions,
  ThemeProvider as MuiThemeProvider,
  responsiveFontSizes,
} from '@mui/material';
import type { Theme } from '@mui/material';
import { deepmerge } from '@mui/utils';
import {
  wellframeLightTheme,
  hphcLightTheme,
  bcbsKansasCityLightTheme,
  bcbsSouthCarolinaLightTheme,
  hscsnLightTheme,
  bcbsTennesseeLightTheme,
  careFirstLightTheme,
  careElevateLightTheme,
} from 'themes/themes';
import { APP_LIGHT_MODE } from 'themes/constants';
import { CobrandIdentifier } from 'features/cobrand/types';
import { getInitialTheme } from 'themes/utils/themeUtils';

const cobrandThemes = new Map<string, ThemeOptions>([
  ['wellframelight', wellframeLightTheme],
  [`${CobrandIdentifier.HARVARD_PILGRIM}light`, hphcLightTheme],
  [`${CobrandIdentifier.BCBS_KANSAS_CITY}light`, bcbsKansasCityLightTheme],
  [`${CobrandIdentifier.BCBS_SOUTH_CAROLINA}light`, bcbsSouthCarolinaLightTheme],
  [`${CobrandIdentifier.BCBS_TENNESSEE}light`, bcbsTennesseeLightTheme],
  [`${CobrandIdentifier.HSCSN}light`, hscsnLightTheme],
  [`${CobrandIdentifier.CARE_FIRST}light`, careFirstLightTheme],
  [`${CobrandIdentifier.CARE_ELEVATE}light`, careElevateLightTheme],
]);

type ColorMode = 'light' | 'dark';

interface ThemeProviderProps {
  theme: Theme;
  setCobrandTheme: (cobrandName: CobrandIdentifier) => void;
  updateTheme: (theme: Partial<ThemeOptions>) => void;
  updateColorMode: (mode: ColorMode) => void;
}

export const ThemeContext = createContext<ThemeProviderProps>({
  theme: createTheme(wellframeLightTheme),
  setCobrandTheme: (cobrandName) => {},
  updateTheme: (theme) => {},
  updateColorMode: (mode) => {},
});

export const ThemeProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const createThemeWithResponsiveFontSize = (themeOptions: ThemeOptions) =>
    responsiveFontSizes(createTheme(themeOptions));
  const [theme, setTheme] = useState<Theme>(getInitialTheme());
  const [colorMode, setColorMode] = useState<ColorMode>(APP_LIGHT_MODE);

  const setCobrandTheme = useCallback(
    (cobrandName: string) => {
      const cobrandTheme = cobrandThemes.get(`${cobrandName}${colorMode}`);

      if (cobrandTheme) {
        setTheme(createThemeWithResponsiveFontSize(cobrandTheme));
      } else {
        // TODO: Remove with logging service
        /* eslint-disable no-console */
        console.log(
          `Failed to load theme for ${cobrandName} cobrand. Setting default wellframe theme`,
        );
        setTheme(createThemeWithResponsiveFontSize(wellframeLightTheme));
      }
    },
    [colorMode],
  );

  const updateTheme = useCallback((newTheme: Partial<ThemeOptions>) => {
    setTheme((currentTheme) => {
      const modifiedTheme = createThemeWithResponsiveFontSize(deepmerge(currentTheme, newTheme));
      return modifiedTheme;
    });
  }, []);

  const updateColorMode = useCallback((newMode: ColorMode) => {
    setColorMode(newMode);
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, setCobrandTheme, updateTheme, updateColorMode }}>
      <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
